import { useQuery } from '@tanstack/react-query';
import {
  BillingKeys,
  getBillingInfoModelQuery,
  getHubSpotCompanyQuery,
  getStripeKeyDetails,
} from '../../../api/billing-api';
import PaymentMethodsList from './payment-methods-list';
import { StripeProvider } from './stripe-provider';
import { useUserSettings } from '../../../utils/use-user-settings';
import { Messages } from '../../../utils/message-constants';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Skeleton from '@mui/material/Skeleton';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import SharedButton from '../../../shared/buttons/shared-button';
import AddCard from '@mui/icons-material/AddCard';
import Payments from '@mui/icons-material/Payments';
import Refresh from '@mui/icons-material/Refresh';
import SharedTabHeader from '../../../shared/shared-tab-header';

export default function PaymentMethodsPage() {
  const { billingRoles, changeLogRoles } = useUserSettings();

  const {
    isLoading: isLoadingBillingInfo,
    isFetching: isFetchingBillingInfo,
    data: billingInformationModel,
  } = useQuery(getBillingInfoModelQuery());

  const { isLoading: isLoadingHubSpotCompany, data: hubSpotCompany } = useQuery(
    getHubSpotCompanyQuery()
  );

  const isEnabled =
    Boolean(billingInformationModel) &&
    !isLoadingBillingInfo &&
    !isFetchingBillingInfo;

  const qbInstance = billingInformationModel?.qbInstance;

  const { data: stripeKeyDetails, isLoading: isLoadingStripeKeyDetails } =
    useQuery({
      queryKey: [BillingKeys.StripeKeyType, qbInstance],
      queryFn: () => getStripeKeyDetails(qbInstance!),
      enabled: isEnabled,
      meta: {
        errorMessage: Messages.Errors.Network.Loading('stripe information'),
      },
    });

  const descriptionText =
    'You can add credit card or bank account payment options for a quicker checkout process when ordering additional licenses. Bank accounts will need to be validated before being able to use them as a payment method. You can also manage autopay options for your subscriptions by clicking on the "Update Autopay" icon button above.';

  if (
    isLoadingBillingInfo ||
    isFetchingBillingInfo ||
    isLoadingStripeKeyDetails ||
    isLoadingHubSpotCompany
  ) {
    return (
      <Box sx={{ position: 'relative', minHeight: 500 }}>
        <SharedTabHeader title="Payment Methods">
          <Stack
            direction="row"
            gap={1}
            alignItems="center"
            justifyContent="center"
          >
            <Tooltip arrow title="Update Autopay">
              <span>
                <IconButton disabled>
                  <Payments />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip arrow title="Refresh">
              <span>
                <IconButton disabled>
                  <Refresh />
                </IconButton>
              </span>
            </Tooltip>
            <SharedButton variant="primary" endIcon={<AddCard />} disabled>
              Add Payment
            </SharedButton>
          </Stack>
        </SharedTabHeader>
        <Typography variant="body1" sx={{ my: 2, px: 1 }}>
          {descriptionText}
        </Typography>

        <Box sx={{ maxWidth: 1000, mx: 'auto', my: 2 }}>
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton
              variant="rectangular"
              height={80}
              // eslint-disable-next-line react-x/no-array-index-key
              key={index}
              sx={{ my: 1 }}
            />
          ))}
        </Box>
      </Box>
    );
  }

  if (stripeKeyDetails && billingInformationModel && hubSpotCompany) {
    return (
      <StripeProvider stripePublicKey={stripeKeyDetails.publicKey}>
        <PaymentMethodsList
          billingInfo={billingInformationModel}
          changeLogRoles={changeLogRoles}
          billingRoles={billingRoles}
          hubSpotCompany={hubSpotCompany}
          descriptionText={descriptionText}
        />
      </StripeProvider>
    );
  }
}
